import { useCallback, useEffect, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { useStore } from "react-redux";

import { AgGridReact } from "ag-grid-react";
import { Column } from "ag-grid-community";

import { GoAiguaSelectOption } from "types/generic";
import { LANG } from "enums/global.enum";

import {
    getAllGisLayersTable,
    updateGisLayers,
} from "services/requests/v1/gislayer";

import CheckboxCellRenderer from "components/adhoc/gisassignmentscheckboxcellrenderer";
import GoAiguaTextInput from "components/generic/goaiguatextinput";
import { GoAiguaButton, SearchBox } from "components/generic";
import ToastRequestHandler from "components/adhoc/toast-request-handler";

import {
    agGridLocaleEN,
    agGridLocaleES,
} from "translations/ag-grid-translations";
import { useRequestStatusManager } from "utils/hooks/use-request-status-manager.hook";

const GisPageAssignmentTab = () => {
    const intl = useIntl();

    const [textFilter, setTextFilter] = useState("");

    const [gridApi, setGridApi] = useState<any>(null);

    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    const [saveButtonDisabled, setSaveButtonDisabled] = useState<
        "enabled" | "disabled"
    >("disabled");

    const [initialAssigments, setInitialAssigments] = useState<any[]>([]);
    const [edited, setEdited] = useState<boolean>(false);

    const generateColumnDefFromData = () => {
        let columnDefinitions: any[] = [];
        let columnKeys: { [key: string]: boolean } = {};

        for (let rowIndex in rowData) {
            const currentRow = rowData[rowIndex];
            const rowKeys = Object.keys(currentRow);
            for (let keyIndex in rowKeys) {
                columnKeys[rowKeys[keyIndex]] = true;
            }
        }

        if ("path" in columnKeys) {
            delete columnKeys["path"];
        }

        let keyList = Object.keys(columnKeys).sort();
        for (let keyIndex in keyList) {
            if (keyList[keyIndex] !== "layerId") {
                columnDefinitions.push({
                    headerName: keyList[keyIndex],
                    field: keyList[keyIndex],
                    cellRenderer: "checkboxRenderer",
                    cellStyle: {
                        display: "grid",
                        placeItems: "center",
                    },
                });
            }
        }

        return columnDefinitions;
    };

    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState<any>([]);

    const store = useStore();
    const language = store.getState().app.language;

    const contentName = "gispage";

    const {
        allContentIsReady,
        errorList,
        success,
        subscribeContent,
        unsubscribeContent,
        removeAllData,
        setError,
    } = useRequestStatusManager();

    useEffect(() => {
        subscribeContent(contentName);

        const retrieveGisLayers = async () => {
            try {
                const { data } = await getAllGisLayersTable();
                setRowData(data);
                unsubscribeContent(contentName);
            } catch (error) {
                setError(contentName);
            }
        };

        retrieveGisLayers();
    }, []);

    useEffect(() => {
        setColumnDefs(generateColumnDefFromData());
    }, [rowData]);

    useEffect(() => {
        if (gridApi) {
            let assignments: any[] = [];
            gridApi.api.forEachNode((node: any) => {
                if (node && node.data) {
                    let nodeLayers = { ...node.data };
                    delete nodeLayers["path"];
                    assignments.push(nodeLayers);
                }
            });
            setSaveButtonDisabled("disabled");
            setInitialAssigments(assignments);
        }
    }, [columnDefs]);

    useEffect(() => {
        let options: GoAiguaSelectOption[] = [];
        if (gridApi) {
            console.log(gridApi);
            gridApi.columnApi.getAllColumns().forEach((col: Column) => {
                const colId = col.getColId() ? col.getColId() : null;
                const colName = col.getColDef().headerName;
                if (colId && colName) {
                    // @ts-ignore
                    // I do not know why this is not working, colId undefined is being checked on the if
                    options.push({
                        label: colName,
                        value: colId,
                    });
                }
            });

            let assignments: any[] = [];
            gridApi.api.forEachNode((node: any) => {
                if (node && node.data) {
                    let nodeLayers = { ...node.data };
                    delete nodeLayers["path"];
                    assignments.push(nodeLayers);
                }
            });
            setSaveButtonDisabled("disabled");
            setInitialAssigments(assignments);
        }
    }, [gridApi, isInfoModalOpen]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            headerName: intl.formatMessage({ id: "app.gis.layers_and_groups" }),
            minWidth: 300,
            pinned: true
        };
    }, []);

    const getDataPath = useCallback((data) => {
        return data.path;
    }, []);

    const retrieveAssignmentsFromTable = async () => {
        subscribeContent(contentName);

        try {
            let assignments: any[] = [];
            gridApi.api.forEachNode((node: any) => {
                if (node && node.data) {
                    let nodeLayers = { ...node.data };
                    delete nodeLayers["path"];
                    assignments.push(nodeLayers);
                }
            });
            await updateGisLayers(assignments);
            setIsInfoModalOpen(true);
            unsubscribeContent(contentName, true);
        } catch (error) {
            setError(contentName);
        }
    };

    const objectsEqual = (o1: any, o2: any) =>
        Object.keys(o1).length === Object.keys(o2).length &&
        Object.keys(o1).every((p) => o1[p] === o2[p]);

    const arraysEqual = (a1: any, a2: any) =>
        a1.length === a2.length &&
        a1.every((o: any, idx: any) => objectsEqual(o, a2[idx]));

    const changeCheckboxes = () => {
        let assignments: any[] = [];
        gridApi.api.forEachNode((node: any) => {
            if (node && node.data) {
                let nodeLayers = { ...node.data };
                delete nodeLayers["path"];
                assignments.push(nodeLayers);
            }
        });
        const arraysCompared = arraysEqual(assignments, initialAssigments);

        setSaveButtonDisabled(arraysCompared ? "disabled" : "enabled");
    };

    useEffect(() => {
        if (edited) {
            changeCheckboxes();
            setEdited(false);
        }
    }, [edited]);

    const selectTableLocale = () => {
        switch (language) {
            case LANG.EN:
                return agGridLocaleEN;
            case LANG.ES:
                return agGridLocaleES;
            default:
                break;
        }
    };

    const filterRowData = () => {
        return rowData.filter((data: any) =>
            data.path.join(" ").match(new RegExp(textFilter, "gi"))
        );
    };

    return (
        <div className="l-gis-page-assignments-tab">
            <div className="l-gis-page-assignments-tab__container">
                <div className="l-gis-page-assignments-tab__table-header">
                    <div className="l-gis-page-assignments-tab__input-group --search-filter">
                        <label>{`${intl.formatMessage({
                            id: "app.gis.text_filter",
                        })}`}</label>
                        <SearchBox
                            placeholder={`${intl.formatMessage({
                                id: "app.gis.search",
                            })}`}
                            value={textFilter}
                            onChange={setTextFilter}
                        />
                    </div>
                    <div className="l-gis-page-assignments-tab__input-group --solutions-filter"></div>
                </div>
                <div className="l-gis-page-assignments-tab__table-body">
                    {
                        <GoAiguaButton
                            text={intl.formatMessage({
                                id: "app.gis.save_changes",
                            })}
                            className={"l-gis-page-assignments-tab__table-save"}
                            onClick={retrieveAssignmentsFromTable}
                            status={saveButtonDisabled}
                        />
                    }
                    <div className="ag-theme-alpine-dark goaigua-table fit-parent border-box">
                        <AgGridReact
                            rowData={filterRowData()}
                            columnDefs={columnDefs}
                            autoGroupColumnDef={autoGroupColumnDef}
                            animateRows={true}
                            pagination={false}
                            paginationAutoPageSize={true}
                            headerHeight={40}
                            treeData={true}
                            groupDefaultExpanded={-1}
                            getDataPath={getDataPath}
                            frameworkComponents={{
                                checkboxRenderer: (props: any) => (
                                    <CheckboxCellRenderer
                                        {...props}
                                        changeCheckboxes={() => {
                                            setEdited(true);
                                        }}
                                    />
                                ),
                            }}
                            onGridReady={setGridApi}
                            localeText={selectTableLocale()}
                        />
                    </div>
                </div>
            </div>
            <ToastRequestHandler
                allContentIsReady={allContentIsReady}
                closeToast={removeAllData}
                errorList={errorList}
                success={success}
            />
        </div>
    );
};

export default GisPageAssignmentTab;
